<template>
  <div class="orders-show font-inter pb-20 md:py-6 enter-content-animation" v-if="order">
    <!-- Header with Back button -->
    <div class="flex items-center gap-4 mb-6 sticky top-0 bg-white/80 backdrop-blur z-30 py-2 border-b">
        <button @click="setOrder(null)" class="p-2 hover:bg-gray-100 rounded-full transition">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <div>
            <h1 class="text-xl font-bold">Pedido #{{ order.id }}</h1>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider">{{ new Date(order.createdAt).toLocaleDateString('pt-BR') }} às {{ new Date(order.createdAt).toLocaleTimeString('pt-BR').slice(0, 5) }}h</p>
        </div>
        <div class="ml-auto">
            <div :style="{background: statusTranslations[order.status]?.color}" class="text-white text-[10px] uppercase font-bold p-2 px-3 rounded-xl shadow-sm">
                {{ statusTranslations[order.status]?.label }}
            </div>
        </div>
    </div>

    <!-- Main Content Grid -->
    <div class="grid grid-cols-12 gap-6">
        <!-- Items Column -->
        <div class="col-span-12 lg:col-span-8 flex flex-col gap-4">
            <div class="bg-white rounded-3xl p-6 border box-shadow">
                <h2 class="text-lg font-bold mb-4 flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                    Itens do Pedido
                </h2>
                
                <div class="flex flex-col gap-4">
                    <!-- The items are returned as a flat list by the API, so we just list them.
                         Optionally we could group them if we find a product/variant/addon pattern. -->
                    <div v-for="item in order.items" :key="item.id" class="flex items-start justify-between py-3 border-b last:border-0" :class="item.canceledAt ? 'opacity-50' : ''">
                        <div class="flex gap-4 flex-1">
                            <div class="h-12 w-12 rounded-xl bg-gray-50 flex items-center justify-center border overflow-hidden">
                                <img v-if="item.product?.media?.[0]" :src="apiBaseUrl + '/storage/' + item.product.media[0].path" class="w-full h-full object-cover">
                                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-gray-300"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                            </div>
                            <div class="flex-1">
                                <h4 class="font-bold text-gray-800" :class="item.canceledAt ? 'line-through' : ''">{{ item.productName || item.product?.name || (item.variantId ? 'Variante' : 'Adicional') }}</h4>
                                <p class="text-xs text-gray-400">{{ item.quantity }}x {{ (item.promotionalPrice || item.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</p>
                                <p v-if="item.notes" class="text-xs text-gray-500 italic mt-1">Obs: {{ item.notes }}</p>
                                <p v-if="item.canceledAt" class="text-xs text-red-500 font-semibold mt-1">Cancelado</p>
                            </div>
                        </div>
                        <div class="flex flex-col items-end gap-2">
                            <div class="font-bold text-gray-700">
                                {{ ((item.promotionalPrice || item.price) * item.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                            </div>
                            <button
                                v-if="!item.canceledAt && (order.status === 'PENDING' || order.status === 'PREPARING')"
                                @click="cancelItem(item)"
                                class="text-xs font-bold text-red-500 hover:text-red-700 transition"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Notes Section -->
                <div v-if="order.notes" class="mt-6 p-4 bg-yellow-50 rounded-2xl border border-yellow-100 italic text-sm text-yellow-800">
                    <span class="font-bold block mb-1 not-italic text-xs uppercase opacity-60">Observações:</span>
                    "{{ order.notes }}"
                </div>
            </div>

            <!-- Client/Table Info -->
            <div class="bg-white rounded-3xl p-6 border box-shadow">
               <div class="grid grid-cols-2 gap-4">
                   <div v-if="order.customer" class="flex flex-col gap-1">
                       <span class="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Cliente</span>
                       <p class="font-bold text-gray-800">{{ order.customer.name }}</p>
                       <p class="text-xs text-gray-500">{{ order.customer.phone || 'Sem telefone' }}</p>
                   </div>
                   <div v-if="order.tab" class="flex flex-col gap-1">
                       <span class="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Local</span>
                       <p class="font-bold text-gray-800">Mesa {{ order.tab.number }}</p>
                       <p v-if="order.tab.name" class="text-xs text-gray-500">{{ order.tab.name }}</p>
                   </div>
               </div>
            </div>
        </div>

        <!-- Summary Column -->
        <div class="col-span-12 lg:col-span-4 flex flex-col gap-4">
            <div class="bg-zinc-900 rounded-3xl p-6 text-white box-shadow">
                <h2 class="text-lg font-bold mb-6 flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    Resumo de Pagamento
                </h2>

                <div class="flex flex-col gap-4 mb-6">
                    <div class="flex justify-between items-center opacity-70 text-sm">
                        <span>Subtotal</span>
                        <span>{{ order.subtotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div v-if="order.discount" class="flex justify-between items-center text-red-400 text-sm">
                        <span>Desconto</span>
                        <span>-{{ order.discount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div v-if="order.deliveryFee" class="flex justify-between items-center opacity-70 text-sm">
                        <span>Entrega</span>
                        <span>{{ order.deliveryFee.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                    <div class="flex justify-between items-center pt-4 border-t border-white/10">
                        <span class="font-bold text-lg">Total</span>
                        <span class="font-bold text-2xl text-success">{{ order.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </div>
                </div>

                <!-- Payment Details -->
                <div class="bg-white/5 rounded-2xl p-4 flex flex-col gap-3">
                    <div class="flex items-center justify-between text-xs">
                        <span class="opacity-50 uppercase font-bold">Método</span>
                        <span class="font-bold uppercase">{{ paymentMethodTranslations[order.paymentMethod] || 'N/A' }}</span>
                    </div>
                    <div class="flex items-center justify-between text-xs">
                        <span class="opacity-50 uppercase font-bold">Status</span>
                        <span :class="order.paymentStatus === 'paid' ? 'text-success' : 'text-yellow-400'" class="font-bold uppercase">
                            {{ paymentStatusTranslations[order.paymentStatus] }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex flex-col gap-3">
                <button v-if="order.status === 'PENDING'" @click="updateStatus('PREPARING')" class="w-full bg-primary text-white p-4 rounded-2xl font-bold hover:bg-opacity-90 transition">
                    Aceitar e Preparar
                </button>
                <button v-if="order.status === 'PREPARING'" @click="updateStatus('PREPARED')" class="w-full bg-primary text-white p-4 rounded-2xl font-bold hover:bg-opacity-90 transition">
                    Marcar como Pronto
                </button>
                <button v-if="order.status === 'PREPARED'" @click="updateStatus('DELIVERED')" class="w-full bg-primary text-white p-4 rounded-2xl font-bold hover:bg-opacity-90 transition">
                    Entregar / Concluir
                </button>

                <button @click="reprintReceipt" class="w-full bg-blue-50 p-4 rounded-2xl font-bold text-blue-600 border border-blue-100 hover:bg-blue-100 transition flex items-center justify-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="8" height="5" x="8" y="11"/></svg>
                    Reimprimir
                </button>

                <button v-if="order.status !== 'CANCELED' && order.status !== 'COMPLETED' && order.status !== 'DELIVERED'"
                        @click="cancelOrder"
                        class="w-full bg-red-50 p-4 rounded-2xl font-bold text-red-500 border border-red-100 hover:bg-red-100 transition">
                    Cancelar Pedido
                </button>
            </div>
        </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { } from 'vue';
import { apiBaseUrl } from '@/global/state/GlobalState';
import axiosClient from '@/api/axios';
import { useToast } from 'vue-toastification';

const props = defineProps<{order: any, setOrder: Function}>()
const toast = useToast()

const statusTranslations: any = {
    'PENDING': {label: 'pendente', color:'#918373'},
    'PREPARING': {label: 'preparando', color:'#FF9B3D'},
    'PREPARED': {label: 'preparado', color:'#59A6F8'},
    'DELIVERED': {label: 'entregue', color: '#00c9a7'},
    'CANCELED': {label: 'cancelado', color:'#F83535'},
    'COMPLETED': {label: 'concluído', color: '#00c9a7'},
}

const paymentMethodTranslations: any = {
    'card': 'Cartão',
    'pix': 'PIX',
    'cash': 'Dinheiro',
    'credit_card': 'C. Crédito',
    'debit_card': 'C. Débito',
}

const paymentStatusTranslations: any = {
    'pending': 'Pendente',
    'paid': 'Pago',
    'refunded': 'Reembolsado',
    'canceled': 'Cancelado',
    'processing': 'Processando',
}

async function updateStatus(newStatus: string) {
    try {
        await axiosClient.patch(`/orders/${props.order.id}`, { status: newStatus })
        toast.success(`Status atualizado para ${statusTranslations[newStatus].label}`)
        props.setOrder({ ...props.order, status: newStatus })
    } catch (error: any) {
        const message = error.response?.data?.errors?.[0]?.message || "Erro ao atualizar pedido"
        toast.error(message)
    }
}

async function cancelOrder() {
    if (!confirm('Tem certeza que deseja cancelar este pedido?')) return

    try {
        await axiosClient.patch(`/orders/${props.order.id}`, { status: 'CANCELED' })
        toast.success("Pedido cancelado com sucesso")
        props.setOrder({ ...props.order, status: 'CANCELED' })
    } catch (error: any) {
        const message = error.response?.data?.errors?.[0]?.message || "Erro ao cancelar pedido"
        toast.error(message)
    }
}

function reprintReceipt() {
    window.print()
}

async function cancelItem(item: any) {
    if (!confirm(`Deseja cancelar "${item.productName || item.product?.name}"?`)) return

    try {
        await axiosClient.patch(`/orders/${props.order.id}/items/${item.id}/cancel`)
        toast.success("Item cancelado com sucesso")
        // Update the item in the order
        const updatedOrder = { ...props.order }
        const itemIndex = updatedOrder.items.findIndex((i: any) => i.id === item.id)
        if (itemIndex !== -1) {
            updatedOrder.items[itemIndex] = { ...updatedOrder.items[itemIndex], canceledAt: new Date() }
        }
        props.setOrder(updatedOrder)
    } catch (error: any) {
        const message = error.response?.data?.errors?.[0]?.message || "Erro ao cancelar item"
        toast.error(message)
    }
}
</script>

<style scoped>
.box-shadow {
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.enter-content-animation {
    animation: slideIn 0.3s ease-out;
}
@keyframes slideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>